/**
 * #1316 — the wrapper around every non-editor deck (the drive views + Bin / Quarantine, Settings, Keybook, Billing on
 * desktop; the matching mobile screens). Two things, in ONE place so no site gets only half:
 *
 * - `SessionScope scopeKey={tenantId}` (owner, option A): the deck remounts at an identity or account session end AND at
 *   a tenant switch, so no state, effect or cache of the old session reaches the new one — the remounted deck's first
 *   requests carry the new identity.
 * - The auth gate: nothing renders while the account is signing out or gone. A sign-out ends the session (the epoch
 *   bumps) while `user` is still the old account — during the awaited server sign-out and until the refresh answers. A
 *   remount then would re-fetch as that account under the NEW session's signal, so its answers are kept: once the
 *   cookie is cleared that 401s and toasts "Authentication required" on the login page.
 *
 * Every account end then loads the page afresh (`reloadAfterAccountEnd`, from `useAuth`); the gate covers the moment
 * before that load. Accepted residual: on `signed-out-elsewhere` (another tab signed the account out) `signingOut` stays
 * false, so the decks can remount ONCE as the old account before the refresh learns the user is gone (and reloads);
 * those requests are refused.
 *
 * Editors and drafts (chat, the single-file view, workspaces, apps) are never wrapped — a remount would discard them.
 */
import type { ReactElement, ReactNode } from 'react'
import { SessionScope } from '@papan/core-ui/ui'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'

export function SessionDeck({ children }: { children: ReactNode }): ReactElement | null {
  const { user, signingOut } = useAuth()
  const { tenantId } = useTenant()
  if (!user || signingOut) return null
  return <SessionScope scopeKey={tenantId ?? ''}>{children}</SessionScope>
}
